import { normalizeFetuses } from './pregnancyNormalize';

// Whose event is this? (REQ-E1/E3, Tim's decision 21.)
//
// A clinical note is addressed to a PERSON, so a prenatal event on a fetus is stored against the
// gestational carrier with `pregnancyId`/`fetusId` naming its real subject. Decision 21 is that such an
// event appears in BOTH places — under the fetus and in the mother's Clinical Records — *clearly
// labelled*, because the clinical reality cuts both ways: an amniocentesis is performed on the mother
// even though the result is about the fetus.
//
// "Clearly labelled" is the whole point, so the labelling lives here, pure and shared, rather than
// being written twice in two surfaces that could then disagree about whose event it is.

// A note that names a prenatal subject at all.
export const isFetusEvent = (note) =>
  !!note && (note.pregnancyId != null || note.fetusId != null);

// A note about one PARTICULAR fetus, as opposed to the pregnancy as a whole.
export const isFetusSpecificEvent = (note) => !!note && note.fetusId != null;

// Find the pregnancy record a note refers to, on the member it is stored against.
export const pregnancyForEvent = (note, carrier) => {
  if (!note?.pregnancyId || !carrier) return null;
  return (
    (carrier.pregnancies || []).find(
      (p) => String(p._id) === String(note.pregnancyId)
    ) || null
  );
};

// Which fetus of that pregnancy, and its position — the position is what the clinician actually reads
// ("fetus 2 of 3"), since a fetus usually has no name.
export const fetusForEvent = (note, carrier) => {
  const record = pregnancyForEvent(note, carrier);
  if (!record) return null;
  const fetuses = normalizeFetuses(record);
  const index = fetuses.findIndex(
    (f) => f && note.fetusId != null && String(f._id) === String(note.fetusId)
  );
  if (index === -1) return null;
  return { record, fetus: fetuses[index], index, total: fetuses.length };
};

// How a fetus is named in a label: its own name if the clinician gave it one, else the symbol text
// they numbered it with (Catherine's stated use), else its position.
export const fetusDisplayName = ({ fetus, index, total }) => {
  const own = (fetus?.name || fetus?.pedigreeSymbolText || '').trim();
  if (own) return own;
  return total > 1 ? `Fetus ${index + 1} of ${total}` : 'Fetus';
};

const fmtDate = (v) => {
  if (!v) return '';
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB');
};

// The subject label shown on the event wherever it appears. Returns '' for an ordinary note about the
// person themselves, so a caller can render the chip only when there is something to say.
//
// Falls back to a generic "Prenatal" label when the referenced record cannot be resolved — a pregnancy
// that was deleted, for instance. Better an honest "this is a prenatal event" than a blank that reads
// as the mother's own.
export const fetusEventSubjectLabel = (note, carrier) => {
  if (!isFetusEvent(note)) return '';
  const resolved = fetusForEvent(note, carrier);
  if (!resolved) {
    const record = pregnancyForEvent(note, carrier);
    const on = fmtDate(record?.dateOfRecord);
    return on ? `Prenatal · pregnancy ${on}` : 'Prenatal';
  }
  const on = fmtDate(resolved.record?.dateOfRecord);
  const who = fetusDisplayName(resolved);
  return on ? `${who} · pregnancy ${on}` : who;
};

// The events belonging to one fetus — the fetus-side view. Matching on `fetusId` alone would miss the
// pregnancy-level events that still concern this fetus, so a pregnancy-level note is included for
// every fetus of that pregnancy.
export const eventsForFetus = (notes, { pregnancyId, fetusId }) =>
  (notes || []).filter((n) => {
    if (!n || n.pregnancyId == null) return false;
    if (String(n.pregnancyId) !== String(pregnancyId)) return false;
    return n.fetusId == null || String(n.fetusId) === String(fetusId);
  });

// The mother's OWN events — what her Clinical Records list shows when prenatal events are filtered
// out. Kept here so "her own" has one definition; decision 21 means the list shows both by default, so
// this exists for callers that need the distinction (a summary count, or a future preference).
export const ownEvents = (notes) =>
  (notes || []).filter((n) => !isFetusEvent(n));

// A per-fetus summary for a pregnancy record, for surfaces that list the record rather than edit it
// (Browse All records). Those surfaces read the LEGACY top-level fields — `outcome`, `fetalSex`,
// `karyotype` — which mirror only the FIRST fetus, so a twin pregnancy showed one fetus and silently
// hid the other. This returns a row per fetus so the record reads truthfully.
//
// Returns [] for a single-fetus pregnancy: there the legacy fields already say everything, and adding a
// redundant "Fetus 1" row to every existing record would be noise.
export const fetusSummaryRows = (record) => {
  const fetuses = normalizeFetuses(record);
  if (fetuses.length < 2) return [];
  return fetuses.map((fetus, index) => ({
    label: fetusDisplayName({ fetus, index, total: fetuses.length }),
    outcome: fetus?.outcome,
    karyotype: fetus?.karyotype,
    // Only an observed sex, never an assumed one.
    gender: fetus?.fetalSexKnown ? fetus.gender : undefined,
    born: fetus?.bornMemberId != null,
    hidden: !!fetus?.hideInPedigree,
    diagnoses: (fetus?.diagnoses || [])
      .map((d) => d?.name || d?.id)
      .filter(Boolean)
  }));
};
